import { TriggerCreate } from "@dots-projects/types";
import { v4 as uuidv4 } from "uuid";
import gif from "./send-survey-gif.gif";

export const sendSurveyTemplate = (
  workspace: string
): {
  trigger: TriggerCreate;
  gif: any;
  title: string;
  description: string;
} => {
  return {
    title: "Community health survey for members",
    description:
      '<p>Get feedback from your community through surveys! Use this template to send a survey to understand how members feel about the community, and learn what you can do to improve.</p><p className="mt-10">Explore the logic of this template in the editor view on the left.</p>',
    gif,
    trigger: {
      name: "Member survey",
      type: "button_click",
      platform: "discord",
      communityId: localStorage.getItem("guild-id") || "",
      authorUserId: localStorage.getItem("discord-id") || "",
      workspace,
      isDraft: true,
      configs: {
        messages: [
          {
            id: uuidv4(),
            buttonIds: [uuidv4()],
          },
        ],
        responseOnError: "Something went wrong, please try again!",
        $$editorButtonClickContent: {
          message: "Please click link below to take the survey!",
          channelId: "$channelId",
          buttonText: "Take the survey",
          buttonEmoji: "👋",
          htmlMessage: "<p>Please click link below to take the survey! </p>",
        },
      },
      steps: [
        {
          id: "send_survey-0-z28EfO",
          name: "Initial experience",
          type: "send_survey",
          configs: {
            type: "buttons",
            message:
              "Overall, how satisfied are you with your most recent interaction with our community?",
            options: [
              {
                id: "na",
                emoji: "1️⃣",
                label: "Extremely satisfied",
                skipDefer: true,
              },
              {
                id: "f710246a-e02b-4ef9-92b7-78c2bb80b60c",
                emoji: "2️⃣",
                label: "Somewhat satisfied",
                skipDefer: true,
              },
              {
                id: "cf22e318-b747-4a98-8d35-f2c4a3ed8788",
                emoji: "3️⃣",
                label: "Neither satisfied nor dissatisfied",
                skipDefer: true,
              },
              {
                id: "de62f8ae-240e-4c77-b637-b3e97d3b548d",
                emoji: "4️⃣",
                label: "Somewhat dissatisfied",
                skipDefer: true,
              },
              {
                id: "49260f82-262a-4e66-a75e-5196703b9f5f",
                emoji: "5️⃣",
                label: "Extremely dissatisfied",
                skipDefer: true,
              },
            ],
            channelId: "{{interaction.channelId}}",
            ephemeral: true,
            $$editorHtmlMessage:
              "<p>Overall, how satisfied are you with your most recent interaction with our community?</p>",
          },
          isEntry: true,
          isGoal: false,
          transitions: [
            {
              targetStepId: "send_message-0-0AlIIQ",
              when: {
                userResponse: {
                  type: "button",
                  userId: "{{user.id}}",
                  buttonId: "na",
                },
              },
            },
            {
              targetStepId: "send_message-0-0AlIIQ",
              when: {
                userResponse: {
                  type: "button",
                  userId: "{{user.id}}",
                  buttonId: "cf22e318-b747-4a98-8d35-f2c4a3ed8788",
                },
              },
            },
            {
              targetStepId: "send_message-0-0AlIIQ",
              when: {
                userResponse: {
                  type: "button",
                  userId: "{{user.id}}",
                  buttonId: "de62f8ae-240e-4c77-b637-b3e97d3b548d",
                },
              },
            },
            {
              targetStepId: "send_message-0-0AlIIQ",
              when: {
                userResponse: {
                  type: "button",
                  userId: "{{user.id}}",
                  buttonId: "f710246a-e02b-4ef9-92b7-78c2bb80b60c",
                },
              },
            },
            {
              targetStepId: "send_message-0-0AlIIQ",
              when: {
                userResponse: {
                  type: "button",
                  userId: "{{user.id}}",
                  buttonId: "49260f82-262a-4e66-a75e-5196703b9f5f",
                },
              },
            },
          ],
        },
        {
          id: "send_message-0-0AlIIQ",
          name: "Send a message",
          type: "send_message",
          configs: {
            message: "Thanks!",
            channelId: "{{interaction.channelId}}",
            ephemeral: true,
            removeUrlEmbeds: false,
            $$editorHtmlMessage: "<p>Thanks!</p>",
          },
          isEntry: false,
          isGoal: false,
          transitions: [
            {
              targetStepId: "send_survey-0-atTCSE",
            },
          ],
        },
        {
          id: "send_survey-0-atTCSE",
          name: "Doing well question",
          type: "send_survey",
          configs: {
            type: "buttons",
            message: "What is one thing we are doing well?",
            options: [
              {
                id: "africa",
                emoji: "🟡",
                label: "Events",
                skipDefer: true,
              },
              {
                id: "na",
                emoji: "🟢",
                label: "Inviting cool members",
                skipDefer: true,
              },
              {
                id: "ae6908e1-c9ab-40ee-a788-95bf91228705",
                label: "Sharing interesting content",
                skipDefer: true,
              },
            ],
            channelId: "{{interaction.channelId}}",
            ephemeral: true,
            $$editorHtmlMessage: "<p>What is one thing we are doing well?</p>",
          },
          isEntry: false,
          isGoal: false,
          transitions: [
            {
              targetStepId: "$step0",
              when: {
                userResponse: {
                  type: "button",
                  userId: "{{user.id}}",
                  buttonId: "africa",
                },
              },
            },
            {
              targetStepId: "$step0",
              when: {
                userResponse: {
                  type: "button",
                  userId: "{{user.id}}",
                  buttonId: "na",
                },
              },
            },
            {
              targetStepId: "$step0",
              when: {
                userResponse: {
                  type: "button",
                  userId: "{{user.id}}",
                  buttonId: "ae6908e1-c9ab-40ee-a788-95bf91228705",
                },
              },
            },
          ],
        },
        {
          id: "$step0",
          name: "What can be improved",
          type: "open_modal",
          configs: {
            title: "What can be improved?",
            fields: [
              {
                id: "$step0",
                type: "open_modal",
                label: "Please share one candid improvement!",
                style: "paragraph",
              },
            ],
          },
          isEntry: false,
          isGoal: false,
          transitions: [
            {
              targetStepId: "send_message-0-GQ1sNw",
              when: {
                userResponse: {
                  type: "modal-submit",
                  userId: "{{user.id}}",
                  modalId: "{{$step0.modalId}}",
                  messageId: "{{$step0.messageId}}",
                },
              },
            },
          ],
        },
        {
          id: "send_message-0-GQ1sNw",
          name: "thanks message",
          type: "send_message",
          configs: {
            message:
              "Thank you so much for taking the survey. It means a lot! ﻿😍",
            channelId: "{{interaction.channelId}}",
            ephemeral: true,
            removeUrlEmbeds: false,
            $$editorHtmlMessage:
              '<p>Thank you so much for taking the survey. It means a lot! <span class="ql-emojiblot" data-name="heart_eyes">﻿<span contenteditable="false"><span class="ap ap-heart_eyes">😍</span></span>﻿</span></p>',
          },
          isEntry: false,
          isGoal: false,
          transitions: [],
        },
      ],
    },
  };
};
